/**
 * A loop answer's words (#2203): the model's markdown, as React elements from an allowlist of its tokens. No HTML ever
 * reaches the page, a link keeps only its text and an image only its alt, so what a model was fed cannot send a reader
 * anywhere or fetch anything. Prose numbers stay plain (#183): a value alone cannot say which claim a number states.
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { marked } from 'marked';

// a token's `text` holds HTML entities; React escapes what it renders
const ENTITIES: Record<string, string> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };
const unescaped = (s: string) => s.replace(/&(?:amp|lt|gt|quot|#39);/g, (e) => ENTITIES[e]);

function inline(tokens: any[] | undefined): React.ReactNode {
  return (tokens || []).map((t, i) => {
    switch (t.type) {
      case 'strong': return <strong key={i}>{inline(t.tokens)}</strong>;
      case 'em': return <em key={i}>{inline(t.tokens)}</em>;
      case 'del': return <del key={i}>{inline(t.tokens)}</del>;
      case 'codespan': return <code key={i}>{unescaped(t.text)}</code>;
      case 'br': return <br key={i} />;
      case 'link': return <React.Fragment key={i}>{inline(t.tokens)}</React.Fragment>;
      case 'image': return <React.Fragment key={i}>{unescaped(t.text || '')}</React.Fragment>;
      case 'html': return <React.Fragment key={i}>{t.raw}</React.Fragment>;
      default:
        return t.tokens?.length
          ? <React.Fragment key={i}>{inline(t.tokens)}</React.Fragment>
          : <React.Fragment key={i}>{unescaped(t.text ?? t.raw ?? '')}</React.Fragment>;
    }
  });
}

const CELL = { padding: '6px 12px', borderBottom: '1px solid var(--sunk)', whiteSpace: 'nowrap' } as const;

function block(t: any, i: number): React.ReactNode {
  switch (t.type) {
    case 'space': case 'hr': return null;
    case 'heading': return <Typography key={i} variant="subtitle2" component="div" sx={{ mt: 1.5, mb: 0.5 }}>{inline(t.tokens)}</Typography>;
    case 'list': {
      const items = t.items.map((item: any, k: number) => <li key={k}>{(item.tokens || []).map((x: any, n: number) => block(x, n))}</li>);
      return <Box key={i} component={t.ordered ? 'ol' : 'ul'} sx={{ mt: 0, mb: 1, pl: 2.5, typography: 'body2', '& li': { mb: 0.25 } }}>{items}</Box>;
    }
    case 'table':
      return (
        <Box key={i} data-testid="loop-words-table" sx={{ overflowX: 'auto', maxWidth: '100%', my: 1.5 }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
            <thead><tr>
              {t.header.map((c: any, k: number) => (
                <th key={k} style={{ ...CELL, fontWeight: 600, borderBottom: '2px solid var(--line)', backgroundColor: 'var(--surface2)', textAlign: t.align?.[k] || (k ? 'right' : 'left') }}>{inline(c.tokens)}</th>
              ))}
            </tr></thead>
            <tbody>
              {t.rows.map((row: any[], r: number) => (
                <tr key={r}>{row.map((c, k) => <td key={k} style={{ ...CELL, textAlign: t.align?.[k] || (k ? 'right' : 'left') }}>{inline(c.tokens)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    case 'blockquote':
      return <Box key={i} component="blockquote" sx={{ m: 0, mb: 1, pl: 1.5, borderLeft: 3, borderColor: 'divider' }}>{(t.tokens || []).map((x: any, n: number) => block(x, n))}</Box>;
    case 'code':
      return <Box key={i} component="pre" sx={{ m: 0, mb: 1, overflowX: 'auto', fontFamily: 'var(--mono)', fontSize: 12.5 }}>{t.text}</Box>;
    case 'html':
      return <Typography key={i} variant="body2" sx={{ mb: 1 }}>{t.raw}</Typography>;
    default: // a paragraph, or a list item's text
      return <Typography key={i} variant="body2" component="div" sx={{ mb: 1, '&:last-child': { mb: 0 } }}>{t.tokens?.length ? inline(t.tokens) : unescaped(t.text ?? t.raw ?? '')}</Typography>;
  }
}

export default function LoopWords({ text }: { text: string }) {
  const tokens = React.useMemo(() => marked.lexer(text, { gfm: true, breaks: true }), [text]);
  return (
    <Box sx={{ mb: 2, maxWidth: '72ch', '& strong': { fontWeight: 600 }, '& code': { fontFamily: 'var(--mono)', fontSize: '0.92em' } }} data-testid="loop-narrative">
      {tokens.map((t, i) => block(t, i))}
    </Box>
  );
}
